import { AudioMessageKeyEnum } from '@cibli/shared';
import { Speaker } from '@micdrop/web';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getAudioMessage } from '@/services/audio.ts';

export const useAudioMessage = (messageKey: AudioMessageKeyEnum) => {
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (Speaker.isPlaying) {
      Speaker.stopAudio();
    }
    if (isMountedRef.current) {
      setIsPlaying(false);
      setIsAudioLoading(false);
    }
  }, []);

  const play = useCallback(async () => {
    cleanup();

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (isMountedRef.current) {
        setIsAudioLoading(true);
        setIsPlaying(true);
      }
      const audioBlob = await getAudioMessage(messageKey, controller.signal);

      if (!isMountedRef.current || controller.signal.aborted) {
        return;
      }

      const audioBuffer = await audioBlob.arrayBuffer();
      await Speaker.start();
      Speaker.playAudio(audioBuffer);

      Speaker.once('StopPlaying', () => {
        if (isMountedRef.current) {
          setIsPlaying(false);
        }
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Error playing welcome audio:', error);
      if (isMountedRef.current) {
        setIsPlaying(false);
      }
    } finally {
      if (isMountedRef.current && !controller.signal.aborted) {
        setIsAudioLoading(false);
      }
    }
  }, [cleanup, messageKey]);

  useEffect(() => {
    isMountedRef.current = true;
    play();

    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [play, cleanup]);

  return {
    isAudioLoading,
    isPlaying,
  };
};
